import { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../hooks/useAuth';
import { useRealtimeMetrics } from '../hooks/useRealtimeData';

interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed';
  created_at: string;
  method?: string;
}

export default function PaymentsPage() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: '', currency: 'THB' });
  const { user } = useAuth();
  const { metrics } = useRealtimeMetrics();

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setPayments([]);
    } catch (error) {
      console.error('Failed to load payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = async () => {
    if (!user?.apiKey) return;
    
    try {
      await api.createPayment({
        amount: parseFloat(newPayment.amount),
        currency: newPayment.currency,
        orderId: `order_${Date.now()}`
      });
      setShowCreateModal(false);
      setNewPayment({ amount: '', currency: 'THB' });
      loadPayments();
    } catch (error) {
      console.error('Failed to create payment:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-success-light text-success';
      case 'pending':
        return 'bg-warning-light text-warning';
      case 'failed':
        return 'bg-danger-light text-danger';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Payments</h1>
          <p className="text-text-secondary">Manage your payment transactions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Create Payment
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-card border border-border-default rounded-xl p-6 shadow-sm">
          <p className="text-text-secondary text-sm">Total Payments</p>
          <p className="text-2xl font-bold text-text-primary">
            {metrics?.transactionMetrics?.completedTransactions || 0}
          </p>
        </div>
        <div className="bg-card border border-border-default rounded-xl p-6 shadow-sm">
          <p className="text-text-secondary text-sm">Completed</p>
          <p className="text-2xl font-bold text-success">
            {metrics?.transactionMetrics?.completedTransactions || 0}
          </p>
        </div>
        <div className="bg-card border border-border-default rounded-xl p-6 shadow-sm">
          <p className="text-text-secondary text-sm">Pending</p>
          <p className="text-2xl font-bold text-warning">
            {metrics?.transactionMetrics?.pendingTransactions || 0}
          </p>
        </div>
        <div className="bg-card border border-border-default rounded-xl p-6 shadow-sm">
          <p className="text-text-secondary text-sm">Total Volume</p>
          <p className="text-2xl font-bold text-primary-600">
            {(metrics?.transactionMetrics?.totalVolume || 0).toLocaleString()} THB
          </p>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-card border border-border-default rounded-lg overflow-hidden shadow-sm">
        <table className="min-w-full divide-y divide-border-default">
          <thead className="bg-bg-secondary">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Payment ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Created
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border-default">
            {payments.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-text-muted">
                  No payments yet. Create your first payment to get started.
                </td>
              </tr>
            ) : (
              payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-bg-secondary transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-text-primary">
                    {payment.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    {payment.amount.toLocaleString()} {payment.currency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                    {new Date(payment.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Payment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-modal">
          <div className="bg-card border border-border-default rounded-lg p-6 w-full max-w-md shadow-xl">
            <h2 className="text-xl font-bold text-text-primary mb-4">Create New Payment</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Amount (THB)
                </label>
                <input
                  type="number"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment(prev => ({ ...prev, amount: e.target.value }))}
                  className="input"
                  placeholder="1000"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Currency
                </label>
                <select
                  value={newPayment.currency}
                  onChange={(e) => setNewPayment(prev => ({ ...prev, currency: e.target.value }))}
                  className="input"
                >
                  <option value="THB">THB</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCreatePayment}
                disabled={loading || !newPayment.amount}
                className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 py-2 border border-border-default rounded-lg hover:bg-bg-secondary transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
